import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EntityAutocomplete } from "@/components/EntityAutocomplete";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Copy, ExternalLink, AlertTriangle } from "lucide-react";

const EMPTY = {
  client_id: "", client_label: "", fecha_servicio: new Date().toISOString().slice(0, 10),
  tarifa_final: "", ruta_factura: "", tipo_carga: "", pedimento: "", referencia_cliente: "",
  destino_final: "", placas: "", caja_numero: "", numero_viaje_dat: "", folio_cfdi: "",
};

function Field({ label, children, className = "" }) {
  return <div className={className}><Label className="text-xs text-muted-foreground">{label}</Label><div className="mt-1">{children}</div></div>;
}

export function FacturaFormSheet({ open, onOpenChange, companyId, load, onCreated }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(null);
  const [dup, setDup] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [montoRevisado, setMontoRevisado] = useState(false);   // P39
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setPreview(null); setDup(null); setResultado(null); setMontoRevisado(false);
    if (load) {
      api.get(`/facturacion-qbo/loads/${load.id}/precarga`).then(({ data }) => {
        setForm({ ...EMPTY, ...data, client_label: data.client_nombre, tarifa_final: String(data.tarifa_final ?? "") });
      }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    } else {
      setForm(EMPTY);
    }
  }, [open, load]);

  const buildPayload = (forzar = false) => ({
    company_id: companyId, load_id: load?.id || null, client_id: form.client_id || null,
    fecha_servicio: form.fecha_servicio, tarifa_final: parseFloat(form.tarifa_final) || 0,
    ruta_factura: form.ruta_factura, tipo_carga: form.tipo_carga, pedimento: form.pedimento,
    referencia_cliente: form.referencia_cliente, destino_final: form.destino_final, placas: form.placas,
    caja_numero: form.caja_numero, numero_viaje_dat: form.numero_viaje_dat, folio_cfdi: form.folio_cfdi,
    forzar_duplicado: forzar, monto_revisado: montoRevisado,
  });

  const verPreview = async () => {
    setPreviewing(true); setDup(null);
    try {
      const { data } = await api.post("/facturacion-qbo/preview", buildPayload());
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setPreviewing(false); }
  };

  const crear = async (forzar = false) => {
    setCreating(true); setDup(null);
    try {
      const { data } = await api.post("/facturacion-qbo/crear", buildPayload(forzar));
      setResultado(data);
      toast.success(`Factura ${data.qbo_doc_number} creada${data.modo === "simulada" ? " (simulada)" : ""}`);
      onCreated?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // P39: también los duplicados encontrados en QuickBooks (P38) y los posibles (P39): el Dueño los puede forzar.
      if (["duplicado", "duplicado_qbo", "posible_duplicado_qbo"].includes(detail?.code)) setDup(detail);
      else toast.error(formatApiErrorDetail(detail));
    } finally { setCreating(false); }
  };

  const copiarViaje = () => {
    navigator.clipboard.writeText(form.numero_viaje_dat || "");
    toast.success("Número de viaje copiado");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="factura-qbo-sheet">
        <SheetHeader>
          <SheetTitle>{load ? `Facturar ${load.numero_load}` : "Factura directa (sin viaje)"}</SheetTitle>
        </SheetHeader>

        {resultado ? (
          <div className="mt-6 space-y-3" data-testid="factura-qbo-resultado">
            <p className="text-sm">
              Factura <b>{resultado.qbo_doc_number}</b> creada
              {resultado.modo === "simulada" ? " en modo simulación (no se tocó QuickBooks real)." : "."}
            </p>
            {resultado.url_quickbooks && (
              <a href={resultado.url_quickbooks} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary underline" data-testid="factura-qbo-link">
                Abrir en QuickBooks <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            <Button size="sm" onClick={() => onOpenChange(false)} data-testid="factura-qbo-cerrar">Cerrar</Button>
          </div>
        ) : (
          <>
            {load && (form.cliente_origen || form.monto_sugerido) && (
              <div className="mt-4 space-y-2 rounded-md bg-amber-50 p-3 text-xs text-amber-900" data-testid="factura-qbo-sugerido">
                {form.cliente_origen && (
                  <p data-testid="factura-qbo-cliente-origen">
                    {/* P41: "salió de el formato" → "salió del formato" */}
                    Cliente <b>{form.client_nombre}</b>: salió {/^el /i.test(form.cliente_origen || "") ? `del ${form.cliente_origen.slice(3)}` : `de ${form.cliente_origen}`}.
                  </p>
                )}
                {form.monto_sugerido && (
                  <>
                    <p data-testid="factura-qbo-monto-origen">{form.monto_origen}</p>
                    <label className="flex items-center gap-2 font-medium">
                      <Checkbox checked={montoRevisado} onCheckedChange={(v) => setMontoRevisado(!!v)} data-testid="factura-qbo-monto-revisado" />
                      Revisé la tarifa y el cliente
                    </label>
                  </>
                )}
              </div>
            )}
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {!load && (
                <Field label="Cliente" className="sm:col-span-2">
                  <EntityAutocomplete type="clients" value={form.client_id} label={form.client_label}
                    onSelect={(c) => setForm((f) => ({ ...f, client_id: c.id, client_label: `${c.codigo} — ${c.razon_social}` }))}
                    testId="factura-qbo-client" />
                </Field>
              )}
              <Field label="Fecha del servicio">
                <Input type="date" value={form.fecha_servicio} onChange={(e) => set("fecha_servicio", e.target.value)} data-testid="factura-qbo-fecha" />
              </Field>
              <Field label="Tarifa final al cliente (obligatoria)">
                <Input type="number" step="0.01" value={form.tarifa_final} onChange={(e) => set("tarifa_final", e.target.value)} data-testid="factura-qbo-tarifa" />
              </Field>
              <Field label="Ruta completa para la factura (editable)" className="sm:col-span-2">
                <Input value={form.ruta_factura} onChange={(e) => set("ruta_factura", e.target.value)} data-testid="factura-qbo-ruta" />
              </Field>
              <Field label="Tipo de carga / descripción">
                <Input value={form.tipo_carga} onChange={(e) => set("tipo_carga", e.target.value)} placeholder="Ej. PARTES" data-testid="factura-qbo-tipo-carga" />
              </Field>
              <Field label="Pedimento">
                <Input value={form.pedimento} onChange={(e) => set("pedimento", e.target.value)} data-testid="factura-qbo-pedimento" />
              </Field>
              <Field label="Referencia del cliente (obligatoria)">
                <Input value={form.referencia_cliente} onChange={(e) => set("referencia_cliente", e.target.value)} data-testid="factura-qbo-referencia" />
              </Field>
              <Field label="Destino final (para 'SE ENTREGA EN')">
                <Input value={form.destino_final} onChange={(e) => set("destino_final", e.target.value)} data-testid="factura-qbo-destino" />
              </Field>
              <Field label="Placas">
                <Input value={form.placas} onChange={(e) => set("placas", e.target.value)} data-testid="factura-qbo-placas" />
              </Field>
              <Field label="Número de caja">
                <Input value={form.caja_numero} onChange={(e) => set("caja_numero", e.target.value)} data-testid="factura-qbo-caja" />
              </Field>
              <Field label="Número de viaje DAT (opcional)">
                <Input value={form.numero_viaje_dat} onChange={(e) => set("numero_viaje_dat", e.target.value)} data-testid="factura-qbo-viaje-dat" />
              </Field>
              <Field label="Folio CFDI (opcional)">
                <Input value={form.folio_cfdi} onChange={(e) => set("folio_cfdi", e.target.value)} data-testid="factura-qbo-folio" />
              </Field>
            </div>

            {form.numero_viaje_dat && (
              <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-3 text-xs text-amber-800" data-testid="factura-qbo-tag-note">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="flex-1">
                  El número de viaje no se puede escribir en el Tag de QuickBooks (la API no lo permite). Queda en la
                  línea "V: {form.numero_viaje_dat}" y en las notas internas. Si lo necesitás en Tags, copialo y pegalo a mano.
                </div>
                <Button size="sm" variant="outline" className="shrink-0 gap-1" onClick={copiarViaje} data-testid="factura-qbo-copiar-viaje">
                  <Copy className="h-3.5 w-3.5" /> Copiar
                </Button>
              </div>
            )}

            {dup && (
              <div className="mt-3 space-y-2 rounded-md bg-red-50 p-3 text-sm text-red-800" data-testid="factura-qbo-duplicado">
                <p>{dup.message}</p>
                {user?.rol === "dueno" && (
                  <Button size="sm" variant="outline" onClick={() => crear(true)} data-testid="factura-qbo-forzar-duplicado">
                    Crear igual (duplicado consciente)
                  </Button>
                )}
              </div>
            )}

            {preview && (
              <div className="mt-4 rounded-md border border-border bg-slate-50 p-3" data-testid="factura-qbo-preview">
                <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Así va a quedar la factura</p>
                <ol className="ml-4 list-decimal space-y-1 text-sm">
                  {preview.lineas_legibles.map((l, i) => <li key={i} data-testid={`factura-qbo-preview-linea-${i}`}>{l}</li>)}
                </ol>
                <p className="mt-2 text-xs text-muted-foreground">Nota al cliente: {preview.payload.CustomerMemo.value}</p>
                {preview.simulado && <p className="mt-1 text-xs text-amber-700">Modo simulación — no se enviará nada a QuickBooks real.</p>}
              </div>
            )}

            <SheetFooter className="mt-6 gap-2">
              <Button variant="outline" onClick={verPreview} disabled={previewing} data-testid="factura-qbo-ver-preview">
                {previewing ? "Armando..." : "Ver cómo va a quedar"}
              </Button>
              <Button onClick={() => crear(false)} disabled={creating || (form.monto_sugerido && !montoRevisado)} data-testid="factura-qbo-crear">
                {creating ? "Creando..." : "Crear en QuickBooks"}
              </Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
